import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast, TOAST_TYPE, TOAST_PRIORITY, TOAST_PERSISTENCE } from '../hooks/useToast';
import { useToastSubscriptions } from '../hooks/useToastSubscriptions';
import { supabase } from '../lib/supabase';
import Icon from 'components/AppIcon';

// ── Sound Effects via Web Audio API ──────────────────────────
const playSound = (type = 'ding') => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx?.createOscillator();
    const gain = ctx?.createGain();
    osc?.connect(gain);
    gain?.connect(ctx?.destination);
    if (type === 'alert') {
      osc?.frequency?.setValueAtTime(880, ctx?.currentTime);
      osc?.frequency?.setValueAtTime(660, ctx?.currentTime + 0.1);
      gain?.gain?.setValueAtTime(0.3, ctx?.currentTime);
      gain?.gain?.exponentialRampToValueAtTime(0.001, ctx?.currentTime + 0.4);
      osc?.start(ctx?.currentTime);
      osc?.stop(ctx?.currentTime + 0.4);
    } else {
      osc?.frequency?.setValueAtTime(523, ctx?.currentTime);
      osc?.frequency?.setValueAtTime(659, ctx?.currentTime + 0.1);
      gain?.gain?.setValueAtTime(0.2, ctx?.currentTime);
      gain?.gain?.exponentialRampToValueAtTime(0.001, ctx?.currentTime + 0.3);
      osc?.start(ctx?.currentTime);
      osc?.stop(ctx?.currentTime + 0.3);
    }
  } catch (e) {}
};

// ── Audit log → toast mapping ─────────────────────────────────
const AUDIT_ACTION_MAP = {
  trade_executed:      { type: TOAST_TYPE?.TRADE_SUCCESS, priority: TOAST_PRIORITY?.NORMAL, sound: 'ding', label: 'Trade Executed' },
  trade_failed:        { type: TOAST_TYPE?.TRADE_FAILED,  priority: TOAST_PRIORITY?.NORMAL, sound: null,   label: 'Trade Failed' },
  business_purchased:  { type: TOAST_TYPE?.SUCCESS,       priority: TOAST_PRIORITY?.NORMAL, sound: 'ding', label: 'Business Purchased' },
  business_revenue:    { type: TOAST_TYPE?.SUCCESS,       priority: TOAST_PRIORITY?.LOW,    sound: null,   label: 'Revenue Earned' },
  revenue_tick:        { type: TOAST_TYPE?.SUCCESS,       priority: TOAST_PRIORITY?.LOW,    sound: null,   label: 'Revenue Earned' },
  crime_attempted:     { type: TOAST_TYPE?.INFO,          priority: TOAST_PRIORITY?.NORMAL, sound: null,   label: 'Crime Attempted' },
  level_up:            { type: TOAST_TYPE?.SUCCESS,       priority: TOAST_PRIORITY?.HIGH,   sound: 'ding', label: 'Level Up!' },
  jailed:              { type: TOAST_TYPE?.CRIME_JAIL,    priority: TOAST_PRIORITY?.HIGH,   sound: 'alert',label: 'Jailed' },
};

const PRIORITY_DURATION = {
  [TOAST_PRIORITY?.LOW]:      3000,
  [TOAST_PRIORITY?.NORMAL]:   5000,
  [TOAST_PRIORITY?.HIGH]:     null,   // no auto-dismiss
  [TOAST_PRIORITY?.CRITICAL]: null,
};

// ── Toast History Panel ───────────────────────────────────────
const HISTORY_KEY = 'empirex_toast_history';
const MAX_HISTORY = 50;

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const saveHistory = (items) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items?.slice(0, MAX_HISTORY)));
  } catch {}
};

const FILTER_TYPES = ['all', 'trade', 'crime', 'business', 'faction', 'system'];

const getFilterType = (action = '') => {
  if (action?.includes('trade')) return 'trade';
  if (action?.includes('crime') || action?.includes('jail')) return 'crime';
  if (action?.includes('business') || action?.includes('revenue')) return 'business';
  if (action?.includes('faction') || action?.includes('war')) return 'faction';
  return 'system';
};

export const ToastHistoryPanel = ({ isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (isOpen) setHistory(loadHistory());
  }, [isOpen]);

  const filtered = filter === 'all' ? history : history?.filter(h => h?.filterType === filter);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-md mx-4 rounded-2xl border shadow-2xl overflow-hidden" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={16} color="var(--color-primary)" />
            <span className="font-heading text-sm font-bold text-foreground">Notification History</span>
            <span className="font-caption text-xs text-muted-foreground">({history?.length})</span>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded flex items-center justify-center hover:bg-white/5 transition-colors">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
        {/* Filter tabs */}
        <div className="flex gap-1 px-3 py-2 overflow-x-auto border-b" style={{ borderColor: 'var(--color-border)' }}>
          {FILTER_TYPES?.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-2.5 py-1 rounded-md font-caption text-xs whitespace-nowrap transition-all"
              style={{
                background: filter === f ? 'rgba(0,217,255,0.15)' : 'transparent',
                color: filter === f ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                border: `1px solid ${filter === f ? 'rgba(0,217,255,0.3)' : 'transparent'}`,
              }}
            >
              {f?.charAt(0)?.toUpperCase() + f?.slice(1)}
            </button>
          ))}
        </div>
        {/* List */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 120px)' }}>
          {filtered?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="BellOff" size={24} color="var(--color-muted-foreground)" />
              <p className="font-caption text-sm text-muted-foreground mt-2">No notifications yet</p>
            </div>
          ) : (
            filtered?.map((item, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 border-b hover:bg-white/2 transition-colors" style={{ borderColor: 'var(--color-border)' }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(0,217,255,0.1)' }}>
                  <Icon name={item?.icon || 'Bell'} size={13} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-caption text-xs text-foreground leading-snug">{item?.message}</p>
                  <p className="font-caption text-xs text-muted-foreground mt-0.5">
                    {new Date(item.timestamp)?.toLocaleTimeString()}
                  </p>
                </div>
                <span className="font-caption text-xs px-1.5 py-0.5 rounded capitalize flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--color-muted-foreground)' }}>
                  {item?.filterType}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── DND Mode toggle stored in localStorage ────────────────────
const DND_KEY = 'empirex_dnd_mode';
const getDND = () => { try { return localStorage.getItem(DND_KEY) === 'true'; } catch { return false; } };
const setDND = (v) => { try { localStorage.setItem(DND_KEY, String(v)); } catch {} };

// ── Grouping: batch similar toasts ───────────────────────────
const BATCH_WINDOW = 3000; // ms

// ── ToastManager ─────────────────────────────────────────────
const ToastManager = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [dndMode, setDndMode] = useState(getDND);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [factionId, setFactionId] = useState(null);
  const pendingGroupRef = useRef({}); // action -> { count, amount, timerId }
  const historyRef = useRef(loadHistory());

  // Fetch user's faction
  useEffect(() => {
    if (!user?.id) return;
    supabase?.from('faction_members')?.select('faction_id')?.eq('user_id', user?.id)?.single()?.then(({ data }) => { if (data?.faction_id) setFactionId(data?.faction_id); });
  }, [user?.id]);

  const addToHistory = useCallback((entry) => {
    const updated = [entry, ...historyRef?.current]?.slice(0, MAX_HISTORY);
    historyRef.current = updated;
    saveHistory(updated);
  }, []);

  const fireToast = useCallback((message, type, priority, sound, icon, actions) => {
    if (dndMode && priority !== TOAST_PRIORITY?.CRITICAL) {
      // Still add to history but don't show toast
      return;
    }
    const duration = PRIORITY_DURATION?.[priority] ?? 5000;
    const persistence = (priority === TOAST_PRIORITY?.HIGH || priority === TOAST_PRIORITY?.CRITICAL)
      ? TOAST_PERSISTENCE?.PERSISTENT
      : TOAST_PERSISTENCE?.TRANSIENT;
    showToast(message, type, { duration, persistence, actions });
    if (sound) playSound(sound);
  }, [dndMode, showToast]);

  // ── Audit log handler ─────────────────────────────────────
  const handleAuditLog = useCallback((log) => {
    const action = log?.action || '';
    const meta = log?.metadata || {};
    const cfg = AUDIT_ACTION_MAP?.[action];
    if (!cfg) return;

    const filterType = getFilterType(action);
    let message = '';
    let icon = 'Bell';
    let actions = null;

    // Build message per action type
    if (action === 'trade_executed') {
      const sym = meta?.symbol || meta?.stock_symbol || 'stock';
      const amt = meta?.total_value ? `$${parseFloat(meta.total_value)?.toLocaleString()}` : '';
      message = `Trade executed: ${sym}${amt ? ` · ${amt}` : ''}`;
      icon = 'TrendingUp';
      actions = [{ label: 'View Portfolio', onClick: () => window.location.hash = '/portfolio', primary: true }];
    } else if (action === 'business_purchased') {
      const name = meta?.business_name || meta?.name || 'Business';
      message = `${name} acquired successfully!`;
      icon = 'Building2';
    } else if (action === 'business_revenue' || action === 'revenue_tick') {
      // Group revenue ticks
      const name = meta?.business_name || 'Business';
      const amount = parseFloat(meta?.amount || meta?.revenue || 0);
      const key = 'revenue_group';
      if (pendingGroupRef?.current?.[key]) {
        pendingGroupRef.current[key].count += 1;
        pendingGroupRef.current[key].amount += amount;
        clearTimeout(pendingGroupRef?.current?.[key]?.timerId);
      } else {
        pendingGroupRef.current[key] = { count: 1, amount, names: [name] };
      }
      pendingGroupRef.current[key].timerId = setTimeout(() => {
        const g = pendingGroupRef?.current?.[key];
        if (!g) return;
        const msg = g?.count === 1
          ? `${g?.names?.[0]} earned $${g?.amount?.toLocaleString()}`
          : `${g?.count} businesses generated revenue: $${g?.amount?.toLocaleString()} total`;
        fireToast(msg, TOAST_TYPE?.SUCCESS, TOAST_PRIORITY?.LOW, null, 'DollarSign', null);
        addToHistory({ message: msg, icon: 'DollarSign', filterType: 'business', timestamp: Date.now() });
        delete pendingGroupRef?.current?.[key];
      }, BATCH_WINDOW);
      return; // handled by batch timer
    } else if (action === 'crime_attempted') {
      const success = meta?.success;
      const reward = parseFloat(meta?.reward || 0);
      if (success) {
        message = `Crime successful! Earned $${reward?.toLocaleString()}`;
        icon = 'DollarSign';
      } else {
        const jailMins = meta?.jail_minutes || 0;
        message = `Caught! Jailed for ${jailMins} minutes`;
        icon = 'Lock';
        actions = [{ label: 'Bail Out', onClick: () => { window.location.href = '/jail'; }, primary: true }];
      }
    } else if (action === 'level_up') {
      const lvl = meta?.new_level || meta?.level || '';
      message = `Level Up! You reached level ${lvl}`;
      icon = 'Star';
    } else if (action === 'jailed') {
      const mins = meta?.jail_minutes || 0;
      message = `You were jailed for ${mins} minutes`;
      icon = 'Lock';
      actions = [{ label: 'Bail Out', onClick: () => { window.location.href = '/jail'; }, primary: true }];
    } else {
      message = log?.description || action?.replace(/_/g, ' ');
    }

    if (!message) return;
    fireToast(message, cfg?.type, cfg?.priority, cfg?.sound, icon, actions);
    addToHistory({ message, icon, filterType, timestamp: Date.now() });
  }, [fireToast, addToHistory]);

  // ── Faction message handler ───────────────────────────────
  const handleFactionMessage = useCallback((msg) => {
    let message = `📢 Faction Announcement: ${msg?.content || msg?.message || 'New message from leader'}`;
    fireToast(message, TOAST_TYPE?.INFO, TOAST_PRIORITY?.NORMAL, 'ding', 'MessageSquare', null);
    addToHistory({ message, icon: 'MessageSquare', filterType: 'faction', timestamp: Date.now() });
  }, [fireToast, addToHistory]);

  // ── Faction war handler ───────────────────────────────────
  const handleFactionWar = useCallback((war) => {
    let message = `⚔️ Your faction is under attack!`;
    fireToast(message, TOAST_TYPE?.FACTION_WAR, TOAST_PRIORITY?.CRITICAL, 'alert', 'Swords',
      [{ label: 'View Wars', onClick: () => { window.location.href = '/wars'; }, primary: true }]
    );
    addToHistory({ message, icon: 'Swords', filterType: 'faction', timestamp: Date.now() });
  }, [fireToast, addToHistory]);

  // ── Notification handler ──────────────────────────────────
  const handleNotification = useCallback((notif) => {
    let message = notif?.message || notif?.title || 'New notification';
    const priority = notif?.priority === 'high' ? TOAST_PRIORITY?.HIGH
      : notif?.priority === 'low' ? TOAST_PRIORITY?.LOW
      : TOAST_PRIORITY?.NORMAL;
    fireToast(message, TOAST_TYPE?.INFO, priority, null, 'Bell', null);
    addToHistory({ message, icon: 'Bell', filterType: 'system', timestamp: Date.now() });
  }, [fireToast, addToHistory]);

  useToastSubscriptions({
    userId: user?.id,
    factionId,
    onAuditLog: handleAuditLog,
    onFactionMessage: handleFactionMessage,
    onFactionWar: handleFactionWar,
    onNotification: handleNotification,
    enabled: !!user?.id,
  });

  const toggleDND = useCallback(() => {
    setDndMode(prev => {
      const next = !prev;
      setDND(next);
      return next;
    });
  }, []);

  return (
    <>
      {/* DND + History controls in header area */}
      <div className="fixed top-4 right-16 z-[9998] flex items-center gap-1.5">
        {/* History button */}
        <button
          onClick={() => setHistoryOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border font-caption text-xs transition-all hover:bg-primary/10"
          style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)' }}
          title="Notification history"
        >
          <Icon name="Bell" size={13} color="currentColor" />
          <span className="hidden sm:inline">History</span>
        </button>
        {/* DND toggle */}
        <button
          onClick={toggleDND}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border font-caption text-xs transition-all"
          style={{
            background: dndMode ? 'rgba(239,68,68,0.1)' : 'var(--color-card)',
            borderColor: dndMode ? 'rgba(239,68,68,0.3)' : 'var(--color-border)',
            color: dndMode ? 'var(--color-error)' : 'var(--color-muted-foreground)',
          }}
          title={dndMode ? 'Do Not Disturb: ON (click to disable)' : 'Enable Do Not Disturb'}
        >
          <Icon name={dndMode ? 'BellOff' : 'Bell'} size={13} color="currentColor" />
          <span className="hidden sm:inline">{dndMode ? 'DND' : 'Alerts'}</span>
        </button>
      </div>
      {/* History Panel */}
      <ToastHistoryPanel isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
};

export default ToastManager;
